export type ExistingPhotoOrderItem = {
  kind: 'existing'
  /**
   * Unique stable key for drag ordering.
   * (Do not assume URL uniqueness across all time; id should be unique per item.)
   */
  id: string
  url: string
}

export type NewPhotoOrderItem = {
  kind: 'new'
  /**
   * Unique stable key for drag ordering.
   */
  id: string
}

export type PhotoOrderItem = ExistingPhotoOrderItem | NewPhotoOrderItem

export function toExistingPhotoItems(urls: string[]): ExistingPhotoOrderItem[] {
  return (urls || []).filter(Boolean).map((url, idx) => ({
    kind: 'existing' as const,
    id: `existing_${idx}`,
    url,
  }))
}

export function extractExistingUrls(items: PhotoOrderItem[]): string[] {
  return (items || []).flatMap((it) => (it.kind === 'existing' ? [it.url] : []))
}

export function sameStringArray(a: string[], b: string[]): boolean {
  if (a === b) return true
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false
  }
  return true
}

export function hasExistingPhotosChanged(originalUrls: string[], items: PhotoOrderItem[]): boolean {
  return !sameStringArray(originalUrls || [], extractExistingUrls(items))
}

export function applySavedPhotoUrlsToVehicle(
  vehicle: Record<string, unknown>,
  dealerPhotos: string[],
): Record<string, unknown> {
  return {
    ...vehicle,
    dealer_photos: [...(dealerPhotos || [])],
  }
}

/**
 * Builds the final ordered dealer_photos array by replacing each "new" item with the
 * uploaded public URL for that item's id, preserving the combined order.
 *
 * Throws if a "new" item is missing its uploaded URL, to avoid silently dropping photos.
 */
export function buildFinalPhotoUrls(
  items: PhotoOrderItem[],
  uploadedById: Record<string, string>,
): string[] {
  const out: string[] = []
  for (const it of items || []) {
    if (it.kind === 'existing') {
      if (it.url) out.push(it.url)
      continue
    }

    const url = uploadedById?.[it.id]
    if (!url) {
      throw new Error(`Missing uploaded URL for photo id "${it.id}"`)
    }
    out.push(url)
  }
  return out
}

